import React, { useRef, useEffect, useCallback } from 'react';
import { InputNumber } from 'antd';
import type { InputNumberProps } from 'antd';

/**
 * Wraps Ant Design InputNumber with two extra interaction modes:
 *  - Mouse wheel while hovering  → increment / decrement by `step`
 *  - Middle-mouse-button + drag up/down → scrub the value continuously
 *
 * All props are forwarded to InputNumber unchanged.
 */
const SpinInputNumber: React.FC<InputNumberProps<number>> = (props) => {
  const { value, min, max, step = 1, onChange } = props;
  const wrapRef = useRef<HTMLDivElement>(null);

  // Keep all mutable props in a ref so the imperative handlers below never
  // close over stale values without needing to be recreated.
  const propsRef = useRef({ value, min, max, step, onChange });
  propsRef.current = { value, min, max, step, onChange };

  // ── Shared helper: move the value by `steps` increments ─────────────────
  const applyDelta = useCallback((steps: number) => {
    const { value: v, min: lo, max: hi, step: s, onChange: cb } = propsRef.current;
    const numStep   = typeof s === 'number' ? s : 1;
    const numValue  = typeof v === 'number' ? v : 0;
    let next = numValue + steps * numStep;
    if (typeof lo === 'number') next = Math.max(lo, next);
    if (typeof hi === 'number') next = Math.min(hi, next);
    // Preserve precision implied by step to prevent floating-point drift.
    const decimals = String(numStep).includes('.')
      ? String(numStep).split('.')[1].length
      : 0;
    next = parseFloat(next.toFixed(decimals));
    cb?.(next);
  }, []);

  // ── Mouse wheel (non-passive so preventDefault works) ───────────────────
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      applyDelta(e.deltaY < 0 ? 1 : -1);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [applyDelta]);

  // ── Middle-mouse-button drag ─────────────────────────────────────────────
  const dragRef = useRef<{ startY: number; startValue: number } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 1) return;  // middle button only
    e.preventDefault();          // suppress browser autoscroll indicator
    const numValue = typeof propsRef.current.value === 'number' ? propsRef.current.value : 0;
    dragRef.current = { startY: e.clientY, startValue: numValue };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    document.body.style.cursor = 'ns-resize';
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const { step: s, min: lo, max: hi, onChange: cb } = propsRef.current;
    const numStep = typeof s === 'number' ? s : 1;
    // 4 pixels per step; dragging up increases the value.
    const rawDelta = (dragRef.current.startY - e.clientY) / 4;
    let next = dragRef.current.startValue + rawDelta * numStep;
    if (typeof lo === 'number') next = Math.max(lo, next);
    if (typeof hi === 'number') next = Math.min(hi, next);
    const decimals = String(numStep).includes('.')
      ? String(numStep).split('.')[1].length
      : 0;
    next = parseFloat(next.toFixed(decimals));
    cb?.(next);
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 1) return;
    dragRef.current = null;
    document.body.style.cursor = '';
  }, []);

  return (
    <div
      ref={wrapRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{ display: 'contents' }}
    >
      <InputNumber<number> {...props} />
    </div>
  );
};

export default SpinInputNumber;
